'use client';

import { type ReactElement, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { Drawer, Heading, TextPassage } from '@petsmart-ui/sparky';
import type { WasLoggedUserAddressChangedStruct } from '@libs/common-next';
import {
    AccountDrawerType,
    PageType,
    usePageLayout,
    useLocalStorage,
} from '@libs/common-next';
import type { LoyaltySignIn } from '@libs/contentful';
import { LoginEvents, trackSuccessSignIn } from '@libs/analytics';

import { InterstitialSignIn } from '../InterstitialSignIn';
import { generateVelocityCreateAccountUrl } from './generateCreateAccountUrl';

import './SignInDrawer.scss';
import { WAS_LOGGED_USER_ADDRESS_CHANGED } from '@libs/core';

export function SignInDrawer({
    data,
}: Readonly<{
    data?: LoyaltySignIn;
}>): ReactElement | null {
    const componentClassName = 'sign-in-drawer';

    const {
        accountDrawersOpen,
        removeAccountDrawers,
        signInDrawerProps,
        pageType,
    } = usePageLayout();

    const [_, setWasLoggedUserAddressChangedStruct] =
        useLocalStorage<WasLoggedUserAddressChangedStruct>(
            WAS_LOGGED_USER_ADDRESS_CHANGED
        );

    const pathname = usePathname();

    const createAccountHref = useMemo(() => {
        const { productToAddToWishlist, offerIdToActivate } =
            signInDrawerProps ?? {};

        switch (pageType) {
            case PageType.PLP:
            case PageType.PDP:
                if (productToAddToWishlist) {
                    return generateVelocityCreateAccountUrl(
                        'wishlist',
                        productToAddToWishlist as string
                    );
                }
                return undefined;
            case PageType.HCP:
                if (offerIdToActivate) {
                    return generateVelocityCreateAccountUrl(
                        'offerActivationId',
                        offerIdToActivate as string
                    );
                }
                return undefined;
            default:
                return undefined;
        }
    }, [pageType, signInDrawerProps]);

    const benefitItems = data?.items?.[0]?.fields?.items || [];
    const isActive = accountDrawersOpen?.includes(AccountDrawerType.SIGN_IN);

    return (
        <Drawer
            data-testid="sign-in-drawer"
            align="right"
            isActive={isActive}
            onClose={() => removeAccountDrawers([AccountDrawerType.SIGN_IN])}
            ariaLabelledBy="drawer-heading-1"
            className={componentClassName}
        >
            <Drawer.Header>
                {data?.previewMeta}
                <Heading
                    data-testid="sign-in-drawer-heading-1"
                    id="drawer-heading-1"
                    tagName="h5"
                    size="title"
                >
                    {data?.signInTitle}
                </Heading>
            </Drawer.Header>
            <Drawer.Body>
                <div className={`${componentClassName}__body`}>
                    <InterstitialSignIn
                        itemsTitle={data?.title}
                        benefitItems={benefitItems}
                        isInDrawer
                        createAccountHref={createAccountHref}
                        onSuccessfulSignIn={() => {
                            setWasLoggedUserAddressChangedStruct(undefined);
                            return trackSuccessSignIn(
                                LoginEvents.SIGN_IN_DRAWER,
                                pathname
                            );
                        }}
                        displayAutoshipDescription
                    />
                </div>
            </Drawer.Body>
        </Drawer>
    );
}
